import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { Dropdown } from "bootstrap"
import Sortable from "sortablejs"
import { getCsrfToken } from "../utils/csrf.util"
import { announcePopoverOpen, bindExclusivePopover } from "../utils/exclusive-popover.util"
import { rethrowUnlessAbort } from "../utils/fetch-error.util"
import { COMPACT_VIEWPORT_QUERY, isCompactViewport } from "../utils/pointer.util"
import { cancelPopoverClose, closeWithPopoverMotion } from "../utils/popover-motion.util"
import TableColumnStateStore, {
  AUTO_WIDTH_STORAGE_PREFIX,
  COLUMN_RESIZE_MIN_WIDTH,
  MAX_COLUMN_WIDTH
} from "../utils/table_column_state_store"
import TableQueryState from "../utils/table_query_state"
import TableSelectionStore from "../utils/table_selection_store"
import { consumeToast, queueToast, showToast } from "../utils/toast.util"

const PENDING_TOAST_STORAGE_KEY = "table-interactions-pending-toast"
const FIXED_DROPDOWN_VIEWPORT_PADDING = 16
const FIXED_DROPDOWN_TRIGGER_GAP = 8
const BULK_DOWNLOAD_MIN_LOADING_MS = 1200
const BULK_DOWNLOAD_MAX_LOADING_MS = 8000
const SELECTION_COLUMN_WIDTH = 56
// Written by #positionViewSettingsMenu, cleared as a set — same shape as
// #clearFixedDropdownInlineStyles, which is the other "undo what we inlined" list.
const VIEW_SETTINGS_POSITION_PROPERTIES = ["position", "top", "left", "right", "width", "max-height", "z-index"]
// Fallback only, and deliberately the same 5.2rem the stylesheet itself falls
// back to in `var(--lc-table-actions-column-width, 5.2rem)`. Reached when the
// property resolves to nothing: a detached table, or a shell that declares no
// width of its own.
const ACTIONS_COLUMN_WIDTH_FALLBACK = 52
// Utility columns hold a checkbox and a menu button, never content worth
// measuring, so their widths are declared rather than derived from content.
// `select` mirrors `.lc-table__selection-cell` (5.6rem,
// `_table_columns_controls.scss`). `actions` is deliberately NOT mirrored: it
// varies per table — `.documents-table-shell--wide-actions` is 11.8rem where
// the shared shell is 5.2rem — and a constant here was read straight back as an
// inline width contradicting the stylesheet, leaving the sticky right-offset
// arithmetic working from a number the column did not have.
// #actionsColumnWidth reads the resolved custom property off the cells, so the
// stylesheet stays the single source of truth and the next width edit is one
// place, not two.
const FIXED_UTILITY_COLUMN_KEYS = ["select", "actions"]
const FIRST_STICKY_DATA_COLUMN_MIN_WIDTH = 160
const FIRST_STICKY_DATA_COLUMN_MOBILE_MIN_WIDTH = 120
// How much of the scroll container the pinned columns have to leave free
// before the first data column stops being one of them. On a 393px phone the
// expenses table pinned 56px of selection + ~285px of supplier + 52px of
// actions = the whole viewport: the table still scrolled horizontally, but
// every pixel of the scroll window sat under a pinned cell, so dragging it
// revealed nothing. That reads as "the table doesn't scroll" — the columns
// behind the pin are simply unreachable. The two utility columns are ~108px of
// fixed width holding the row's checkbox and its menu, so it is always the
// content column that has to stand down.
//
// A free width rather than a share of the container, because that is what the
// requirement actually is: a window wide enough to bring a hidden column into
// view. A share moves the budget with the viewport for no reason anyone can
// state — nobody needs 512px of window on a 1280px screen. Same units and same
// kind of judgement as FIRST_STICKY_DATA_COLUMN_MOBILE_MIN_WIDTH above, and
// deliberately the same number: one column's worth.
const STICKY_COLUMNS_MIN_SCROLL_WINDOW = 120
const INTERACTIVE_SELECTOR = [
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "label",
  "[role='button']",
  "[contenteditable='true']",
  "[data-table-interactions-ignore]",
  // The whole wrapper, not just the toggle and the menu: on a browser without
  // the Popover API the sheet's dim is a ::before on `.dropdown` (see
  // _table_shell_layout.scss), and a pseudo-element hit-tests as its originating
  // element — so without this, the tap that dismisses the sheet also navigates
  // to whatever row it landed on. (With the Popover API the dim is the menu
  // itself, which `.dropdown-menu` below already covers.)
  ".dropdown",
  ".dropdown-menu",
  ".dropdown-toggle",
  ".lc-inline-edit",
  ".lc-table-view-settings"
].join(", ")

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  resizeColumnLabel: "Resize column",
  reorderColumnLabel: "Reorder column",
  removeColumnLabel: "Remove column"
}

export default class extends Controller {
  static targets = [
    "table",
    "headerRow",
    "body",
    "rowCheckbox",
    "selectAll",
    "selectionToolbar",
    "selectedCount",
    "viewSettingsButton",
    "viewSettingsMenu",
    "columnsList",
    "columnMainView",
    "addColumnButton",
    "addColumnList",
    "addColumnPanel",
    "addColumnSearch",
    "addColumnEmpty",
    "headerViewport"
  ]

  static values = {
    translations: { type: Object, default: {} },
    storageKey: String,
    legacyStorageKey: String,
    storageVersion: { type: String, default: "v1" },
    bulkCloneUrl: String,
    bulkDownloadUrl: String,
    bulkDownloadIsdocUrl: String,
    bulkExportUrl: String,
    bulkDeleteUrl: String,
    bulkPayUrl: String,
    stickyHeader: { type: Boolean, default: false },
    stickyColumns: { type: Boolean, default: false },
    initialState: Object,
    rowClick: { type: Boolean, default: false },
    selection: { type: Boolean, default: false },
    bulkActions: { type: Boolean, default: false },
    columnResize: { type: Boolean, default: false },
    columnReorder: { type: Boolean, default: false },
    columnVisibility: { type: Boolean, default: false }
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.columns = this.#readColumns()
    this.defaultState = this.#buildDefaultState()
    this.columnStateStore = new TableColumnStateStore({
      columns: this.columns,
      defaultState: this.defaultState,
      storageKey: this.hasStorageKeyValue ? this.storageKeyValue : null,
      legacyStorageKey: this.hasLegacyStorageKeyValue ? this.legacyStorageKeyValue : null,
      storageVersion: this.storageVersionValue,
      initialState: this.hasInitialStateValue ? this.initialStateValue : undefined
    })
    this.state = this.#loadState()
    this.autoWidths = {}
    this.autoWidthScope = null
    this.columnWidthsCacheable = true
    this.columnWidthsDirty = true
    // Declared with the rest of the per-connection state rather than created on
    // first use, so a reconnected controller starts quiet again — a
    // misconfiguration fixed in the stylesheet and reloaded should be able to
    // report itself once more. See #warnUnconvertibleActionsWidth.
    this.warnedActionsWidths = new Set()
    this.selectionScopeFingerprint = new TableQueryState(window.location.search).fingerprint()
    this.selectionStore = new TableSelectionStore({
      storageKey: this.hasStorageKeyValue ? this.storageKeyValue : null,
      legacyStorageKey: this.hasLegacyStorageKeyValue ? this.legacyStorageKeyValue : null,
      scopeFingerprint: this.selectionScopeFingerprint
    })
    this.#discardLegacySelectionState()
    this.selectedRecords = this.#loadSelectedRecords()
    this.bulkActionInFlight = false
    this.cleanupBulkDownloadLoading = null
    this.pendingColumnControlFocusFrame = null

    this.boundHandleDocumentClick = this.#handleDocumentClick.bind(this)
    this.boundHandleBodyClick = this.#handleBodyClick.bind(this)
    this.boundHandleKeydown = this.#handleKeydown.bind(this)
    this.boundHandleScroll = this.#updateScrollState.bind(this)
    this.boundHandleTableScroll = this.#handleTableScroll.bind(this)
    this.boundHandleFragmentReplaced = this.#handleFragmentReplaced.bind(this)
    this.boundHandleViewSettingsViewportScroll = this.#positionViewSettingsMenu.bind(this)
    this.boundBodyClickTargets = new Set()

    this.#setupFixedDropdowns()
    this.#watchCompactViewport()
    if (this.hasViewSettingsMenuTarget) {
      this.unbindExclusivePopover = bindExclusivePopover(this.element, () => this.#closeViewSettings())
      document.addEventListener("click", this.boundHandleDocumentClick, true)
      window.addEventListener("scroll", this.boundHandleViewSettingsViewportScroll, true)
    }
    if (this.selectionValue || this.hasViewSettingsMenuTarget) {
      document.addEventListener("keydown", this.boundHandleKeydown)
    }
    this.#syncBodyClickListeners()
    this.#setupScrollTracking()

    this.#applyState()
    consumeToast(PENDING_TOAST_STORAGE_KEY)
    if (this.columnReorderValue) {
      this.#setupDragHandles()
      this.#setupHeaderSorting()
    }
    if (this.columnVisibilityValue || this.columnReorderValue) {
      this.#renderColumnMenus()
    }
    if (this.selectionValue) {
      this.#applySelectionState()
      this.updateSelectionState()
    }
    this.#updateScrollState()

    // Last, so the observer never sees this controller's OWN setup work.
    // #applyState reorders cells, which is a childList mutation on this.element
    // and looks exactly like a replaced Turbo fragment — starting the observer
    // before it made the controller reconcile itself on connect. That
    // self-inflicted reconcile captured the scroll offset as it was during
    // connect (0) and queued a restore for the next animation frame, so a
    // scroll that happened in between was overwritten one frame later. Nothing
    // external can replace a fragment during connect, so there is no window to
    // lose by observing from here.
    this.#setupFragmentReconciliation()
  }

  disconnect() {
    if (this.hasViewSettingsMenuTarget) {
      document.removeEventListener("click", this.boundHandleDocumentClick, true)
      window.removeEventListener("scroll", this.boundHandleViewSettingsViewportScroll, true)
    }
    this.unbindExclusivePopover?.()
    if (this.selectionValue || this.hasViewSettingsMenuTarget) {
      document.removeEventListener("keydown", this.boundHandleKeydown)
    }
    this.boundBodyClickTargets?.forEach((body) => body.removeEventListener("click", this.boundHandleBodyClick))
    this.boundBodyClickTargets?.clear()
    this.verticalScrollContainer?.removeEventListener("scroll", this.boundHandleTableScroll)
    window.removeEventListener("resize", this.boundHandleScroll)
    this.tableResizeObserver?.disconnect()
    this.#destroyHeaderSortable()
    this.#destroyMenuSortable()
    this.cleanupBulkDownloadLoading?.()
    this.#unwatchCompactViewport()
    this.#teardownFixedDropdowns()
    this.#teardownResizers()
    this.#teardownDragHandles()
    this.fragmentObserver?.disconnect()
    document.removeEventListener("lucanto:table-fragment-replaced", this.boundHandleFragmentReplaced)
    if (this.pendingFragmentReconciliationFrame !== null) {
      cancelAnimationFrame(this.pendingFragmentReconciliationFrame)
      this.pendingFragmentReconciliationFrame = null
    }
    if (this.pendingFragmentScrollRestoreFrame !== null) {
      cancelAnimationFrame(this.pendingFragmentScrollRestoreFrame)
      this.pendingFragmentScrollRestoreFrame = null
    }
    if (this.pendingColumnControlFocusFrame !== null) {
      cancelAnimationFrame(this.pendingColumnControlFocusFrame)
      this.pendingColumnControlFocusFrame = null
    }
  }

  toggleAllSelection(event) {
    if (!this.selectionValue) return
    const checked = event.currentTarget.checked
    this.rowCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = checked
      this.#toggleRowSelectionState(checkbox)
      this.#syncSelectionRecord(checkbox)
    })
    this.updateSelectionState()
  }

  toggleRowSelection(event) {
    if (!this.selectionValue) return
    this.#toggleRowSelectionState(event.currentTarget)
    this.#syncSelectionRecord(event.currentTarget)
    this.updateSelectionState()
  }

  toggleSelectionCell(event) {
    if (!this.selectionValue) return
    if (event.target.closest("input, button, a, label")) return

    const checkbox = event.currentTarget.querySelector("[data-table-interactions-target='rowCheckbox']")
    if (!checkbox) return

    checkbox.checked = !checkbox.checked
    this.#toggleRowSelectionState(checkbox)
    this.#syncSelectionRecord(checkbox)
    this.updateSelectionState()
  }

  exportColumnState() {
    return this.columnStateStore.export(this.state)
  }

  reconcileTableFragments() {
    const scrollLeft = this.#currentHorizontalScrollLeft()
    this.fragmentObserver?.disconnect()
    try {
      this.#applyState()
      this.#syncBodyClickListeners()
      this.#teardownFixedDropdowns()
      this.#setupFixedDropdowns()
      if (this.selectionValue) {
        this.#applySelectionState()
        this.updateSelectionState()
      }
      this.#restoreHorizontalScrollPosition(scrollLeft)
      this.#updateScrollState()
      this.#scheduleFragmentScrollRestore(scrollLeft)
    } finally {
      this.#observeTableFragments()
    }
  }

  updateSelectionState() {
    if (!this.selectionValue) return
    const selectedCount = this.selectedIds.length

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = selectedCount
    }

    if (this.hasSelectionToolbarTarget) {
      this.selectionToolbarTarget.hidden = selectedCount === 0
    }

    if (this.hasSelectAllTarget) {
      const total = this.rowCheckboxTargets.length
      const selectedOnPageCount = this.rowCheckboxTargets.filter((checkbox) => checkbox.checked).length
      this.selectAllTarget.checked = total > 0 && selectedOnPageCount === total
      this.selectAllTarget.indeterminate = selectedOnPageCount > 0 && selectedOnPageCount < total
    }

    this.element.dataset.hasSelection = selectedCount > 0 ? "true" : "false"
    this.#persistSelectedRecords()
    this.#updateBulkActionAvailability()
  }

  clearSelection(event) {
    if (!this.selectionValue) return
    event?.preventDefault()

    this.#clearSelectedRecords()
    this.#applySelectionState()
    this.updateSelectionState()
  }

  toggleViewSettings(event) {
    if (!this.hasViewSettingsMenuTarget) return
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.dispatchEvent(new CustomEvent("tooltip:reset", { bubbles: true }))
    const isHidden = this.viewSettingsMenuTarget.hidden

    if (isHidden) {
      this.activeViewSettingsButton = event.currentTarget
      announcePopoverOpen(this.element)
      cancelPopoverClose(this.viewSettingsMenuTarget)
      this.viewSettingsMenuTarget.hidden = false
      this.viewSettingsMenuTarget.setAttribute("aria-hidden", "false")
      this.#positionViewSettingsMenu()
      this.#focusViewSettingsEntry()
    } else {
      this.#closeViewSettings()
    }
    this.#syncViewSettingsButtonState(isHidden)
  }

  openAddColumnPanel(event) {
    if (!this.columnVisibilityValue) return
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasAddColumnPanelTarget) return

    if (this.hasViewSettingsMenuTarget) {
      announcePopoverOpen(this.element)
      cancelPopoverClose(this.viewSettingsMenuTarget)
      this.viewSettingsMenuTarget.hidden = false
      this.viewSettingsMenuTarget.setAttribute("aria-hidden", "false")
      this.#syncViewSettingsButtonState(true)
      this.#positionViewSettingsMenu()
    }
    this.#showAddColumnView()
    this.#renderAddColumnMenu()
    this.#positionViewSettingsMenu()
    if (this.hasAddColumnSearchTarget) {
      this.addColumnSearchTarget.focus()
    } else {
      this.#focusViewSettingsEntry(this.addColumnPanelTarget)
    }
  }

  closeAddColumnPanel(event) {
    if (!this.columnVisibilityValue) return
    event.preventDefault()
    event.stopPropagation()
    this.#showColumnMainView({ restoreFocus: true })
  }

  filterAddColumns(event) {
    if (!this.columnVisibilityValue) return
    this.#filterAddColumnOptions(event.currentTarget.value)
  }

  reorderColumnWithKeyboard(event) {
    if (!this.columnReorderValue || !["ArrowLeft", "ArrowRight"].includes(event.key)) return
    const columnKey = event.currentTarget.dataset.columnControlKey
    if (!columnKey) return

    event.preventDefault()
    event.stopPropagation()
    const focusInMenu = event.currentTarget.classList.contains("lc-table-view-settings__drag")
    this.#moveColumnByKeyboard(columnKey, event.key === "ArrowLeft" ? -1 : 1)
    this.#scheduleColumnControlFocus(columnKey, focusInMenu)
  }

  addColumn(event) {
    if (!this.columnVisibilityValue) return
    event.preventDefault()
    event.stopPropagation()

    const key = event.currentTarget.dataset.columnKey
    if (!key) return

    this.state.hidden = this.state.hidden.filter((hiddenKey) => hiddenKey !== key)
    this.#persistState()
    this.#applyState()
    this.#renderColumnMenus()
    this.#renderAddColumnMenu()
    this.#updateScrollState()
    if (this.hasAddColumnPanelTarget && !this.addColumnPanelTarget.hidden) {
      const nextOption = this.addColumnListTarget.querySelector("button:not([hidden])")
      ;(nextOption || (this.hasAddColumnSearchTarget ? this.addColumnSearchTarget : null))?.focus()
    }
  }

  removeColumn(event) {
    if (!this.columnVisibilityValue) return
    event.preventDefault()
    event.stopPropagation()

    const key = event.currentTarget.dataset.columnRemoveKey
    const column = this.columns.find((item) => item.key === key)
    if (!column || column.fixed) return

    if (!this.state.hidden.includes(key)) {
      this.state.hidden.push(key)
      this.#persistState()
      this.#applyState()
      this.#renderColumnMenus()
      this.#renderAddColumnMenu()
      this.#updateScrollState()
    }
  }

  async performBulkAction(event) {
    if (!(this.selectionValue && this.bulkActionsValue)) return
    event.preventDefault()
    event.stopPropagation()
    if (this.bulkActionInFlight) return

    const action = event.currentTarget.dataset.bulkAction
    const ids = this.selectedIds

    if (ids.length === 0) return

    switch (action) {
      case "clone":
        await this.#submitBulkMutation(this.bulkCloneUrlValue, ids)
        break
      case "delete":
        await this.#submitBulkMutation(this.bulkDeleteUrlValue, ids, "DELETE")
        break
      case "download":
        if (this.hasBulkDownloadUrlValue) {
          this.#submitBulkDownload(this.bulkDownloadUrlValue, event.currentTarget)
        } else {
          this.#openSelectedUrls("downloadUrl")
        }
        break
      case "downloadIsdoc":
        if (this.hasBulkDownloadIsdocUrlValue) {
          this.#submitBulkDownload(this.bulkDownloadIsdocUrlValue, event.currentTarget)
        } else {
          this.#openSelectedUrls("downloadIsdocUrl")
        }
        break
      // One button per registry format inside the Export menu; the format itself
      // travels as a data attribute so the toolbar never has to know the list.
      case "export":
        this.#submitBulkExport(event.currentTarget)
        break
      case "pay":
        if (this.hasBulkPayUrlValue) this.#visitBulkPay(ids)
        break
      default:
        break
    }
  }

  // Posts the selection to the registry-driven export endpoint. The server
  // re-resolves and re-authorizes the format, so a tampered data attribute buys
  // nothing; the toast is immediate feedback that a download is on its way,
  // which a `send_data` response cannot give on its own.
  #submitBulkExport(button) {
    if (!this.hasBulkExportUrlValue) {
      showToast({ type: "error", message: "Action could not be completed." })
      return
    }

    const exportFormat = button.dataset.exportFormat
    if (!exportFormat) return

    this.#submitBulkDownload(this.bulkExportUrlValue, button, { export_format: exportFormat })

    const message = button.dataset.exportToast
    if (message) showToast({ type: "success", message })
  }

  // Navigates to the payment order review screen with the selection —
  // a plain GET, the review page re-validates everything server-side.
  #visitBulkPay(ids) {
    const url = new URL(this.bulkPayUrlValue, window.location.origin)
    for (const id of ids) url.searchParams.append("expense_ids[]", id)
    Turbo.visit(url.toString())
  }

  #handleBodyClick(event) {
    if (!this.rowClickValue) return
    const row = event.target.closest("tr[data-row-url]")
    if (!row || this.#isInteractive(event.target)) return

    const url = row.dataset.rowUrl
    if (url) {
      Turbo.visit(url)
    }
  }

  #syncBodyClickListeners() {
    if (!this.rowClickValue) return

    const currentBodies = new Set(this.bodyTargets)
    this.boundBodyClickTargets.forEach((body) => {
      if (currentBodies.has(body)) return
      body.removeEventListener("click", this.boundHandleBodyClick)
      this.boundBodyClickTargets.delete(body)
    })
    currentBodies.forEach((body) => {
      if (this.boundBodyClickTargets.has(body)) return
      body.addEventListener("click", this.boundHandleBodyClick)
      this.boundBodyClickTargets.add(body)
    })
  }

  #handleDocumentClick(event) {
    if (!this.hasViewSettingsMenuTarget) return
    if (this.viewSettingsMenuTarget.hidden) return
    if (this.element.contains(event.target)) return

    this.#closeViewSettings()
  }

  #handleKeydown(event) {
    if (event.key !== "Escape") return
    if (this.hasViewSettingsMenuTarget && !this.viewSettingsMenuTarget.hidden) {
      event.preventDefault()
      if (this.hasAddColumnPanelTarget && !this.addColumnPanelTarget.hidden) {
        this.#showColumnMainView({ restoreFocus: true })
      } else {
        this.#closeViewSettings({ restoreFocus: true })
      }
      return
    }

    if (!this.selectionValue) return
    if (this.selectedIds.length === 0) return
    if (this.#isInteractive(event.target)) return

    this.clearSelection(event)
  }

  #closeViewSettings({ restoreFocus = false } = {}) {
    if (!this.hasViewSettingsMenuTarget) return

    if (!this.viewSettingsMenuTarget.hidden) {
      closeWithPopoverMotion(this.viewSettingsMenuTarget, () => {
        this.viewSettingsMenuTarget.hidden = true
        this.viewSettingsMenuTarget.setAttribute("aria-hidden", "true")
        this.#clearViewSettingsMenuPosition()
        if (restoreFocus) this.activeViewSettingsButton?.focus()
      })
    } else {
      this.#clearViewSettingsMenuPosition()
      if (restoreFocus) this.activeViewSettingsButton?.focus()
    }
    this.#syncViewSettingsButtonState(false)
    this.#showColumnMainView()
  }

  #syncViewSettingsButtonState(isOpen) {
    if (!this.hasViewSettingsButtonTarget) return

    this.viewSettingsButtonTargets.forEach((button) => {
      button.setAttribute("aria-expanded", isOpen.toString())
      button.classList.toggle("is-active", isOpen)
    })
  }

  #focusViewSettingsEntry(container = this.viewSettingsMenuTarget) {
    container?.querySelector('button:not([disabled]):not([hidden]), a[href], [tabindex]:not([tabindex="-1"])')?.focus()
  }

  #scheduleColumnControlFocus(columnKey, focusInMenu) {
    if (this.pendingColumnControlFocusFrame !== null) cancelAnimationFrame(this.pendingColumnControlFocusFrame)
    const focus = () => {
      const selector = focusInMenu
        ? `.lc-table-view-settings__drag[data-column-control-key="${columnKey}"]`
        : `.lc-table__column-drag-handle[data-column-control-key="${columnKey}"]`
      this.element.querySelector(selector)?.focus()
    }
    focus()
    this.pendingColumnControlFocusFrame = requestAnimationFrame(() => {
      this.pendingColumnControlFocusFrame = requestAnimationFrame(() => {
        this.pendingColumnControlFocusFrame = null
        focus()
      })
    })
  }

  #readColumns() {
    return Array.from(this.headerRowTarget.querySelectorAll("[data-column-key]")).map((element) => ({
      key: element.dataset.columnKey,
      label: element.dataset.columnLabel || element.textContent.trim(),
      fixed: element.dataset.columnFixed === "true",
      removable: element.dataset.columnRemovable !== "false",
      defaultHidden: element.dataset.columnDefaultHidden === "true"
    }))
  }

  #buildDefaultState() {
    return {
      hidden: this.columns
        .filter((column) => column.defaultHidden && column.removable && !column.fixed)
        .map((column) => column.key),
      widths: {},
      order: this.columns.map((column) => column.key)
    }
  }

  #setupScrollTracking() {
    if (!((this.stickyHeaderValue || this.stickyColumnsValue) && this.hasTableTarget)) return

    this.verticalScrollContainer = this.element.querySelector(".documents-table-scroll")
    if (!this.verticalScrollContainer) return

    this.verticalScrollContainer.addEventListener("scroll", this.boundHandleTableScroll, { passive: true })
    window.addEventListener("resize", this.boundHandleScroll)

    if ("ResizeObserver" in window) {
      this.tableResizeObserver = new ResizeObserver(this.boundHandleScroll)
      this.tableResizeObserver.observe(this.verticalScrollContainer)
      this.tableResizeObserver.observe(this.tableTarget)
      if (this.hasHeaderViewportTarget) {
        this.tableResizeObserver.observe(this.headerViewportTarget)
      }
    }
  }

  #setupFragmentReconciliation() {
    this.pendingFragmentReconciliationFrame = null
    this.pendingFragmentScrollRestoreFrame = null
    document.addEventListener("lucanto:table-fragment-replaced", this.boundHandleFragmentReplaced)
    if (!("MutationObserver" in window)) return

    this.fragmentObserver = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => this.#mutationAffectsTableFragment(mutation))) return
      this.#scheduleFragmentReconciliation()
    })
    this.#observeTableFragments()
  }

  #observeTableFragments() {
    this.fragmentObserver?.observe(this.element, { childList: true, subtree: true })
  }

  #handleFragmentReplaced(event) {
    const fragment = event.detail?.fragment
    if (fragment && !this.element.contains(fragment)) return
    this.#scheduleFragmentReconciliation()
  }

  #mutationAffectsTableFragment(mutation) {
    return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
      if (!(node instanceof Element)) return false
      return (
        node.matches("tr[data-record-id], td[data-column-key], [data-table-reconcile]") ||
        node.querySelector("tr[data-record-id], td[data-column-key], [data-table-reconcile]")
      )
    })
  }

  #scheduleFragmentReconciliation() {
    if (this.pendingFragmentReconciliationFrame !== null) return
    this.pendingFragmentReconciliationFrame = requestAnimationFrame(() => {
      this.pendingFragmentReconciliationFrame = null
      this.reconcileTableFragments()
    })
  }

  #scheduleFragmentScrollRestore(scrollLeft) {
    if (this.pendingFragmentScrollRestoreFrame !== null) {
      cancelAnimationFrame(this.pendingFragmentScrollRestoreFrame)
    }
    this.pendingFragmentScrollRestoreFrame = requestAnimationFrame(() => {
      this.pendingFragmentScrollRestoreFrame = null
      // Put back only an offset the replacement actually LOST. A frame is long
      // enough for someone to scroll, and re-applying a value captured before
      // that would drag them back — the offset held here is stale the moment
      // the position is no longer the reset-to-zero the replacement caused.
      if (scrollLeft > 0 && this.#currentHorizontalScrollLeft() === 0) {
        this.#restoreHorizontalScrollPosition(scrollLeft)
      }
      this.#updateScrollState()
    })
  }

  #setupFixedDropdowns() {
    this.fixedDropdownInstances = []
    // One decision for the whole pass, so every row in a table presents the
    // same way. Re-run wholesale when the breakpoint is crossed — see
    // #watchCompactViewport.
    const sheetMode = isCompactViewport()

    this.element.querySelectorAll("[data-bs-toggle='dropdown'][data-bs-strategy='fixed']").forEach((toggle) => {
      const menu = toggle.parentElement?.querySelector(".dropdown-menu")
      menu?.classList.add("dropdown-menu--fixed")
      menu?.classList.toggle("dropdown-menu--sheet", sheetMode)

      // A phone gets a bottom sheet, not an anchored popover. Anchoring is the
      // wrong shape there whatever Popper does with it: the menu opens INSIDE
      // the table, over the row it belongs to and over its neighbours, and its
      // own viewport clamp then turns it into a scroll box that reads as cut
      // off. `display: "static"` is Bootstrap's own opt-out: it still creates a
      // Popper instance but disables applyStyles, so no inline position,
      // transform or data-popper-placement is written — which is what lets the
      // stylesheet own the geometry, since an inline style would beat any rule
      // this file could spell.
      if (sheetMode) {
        if (menu) this.#clearFixedDropdownInlineStyles(menu)
        this.fixedDropdownInstances.push(Dropdown.getOrCreateInstance(toggle, { display: "static" }))
        this.#promoteSheetToTopLayer(toggle, menu)
        return
      }

      const placement = this.#fixedDropdownPlacement(toggle)
      const fallbackPlacements =
        placement === "left-start" ? ["left-end", "top-end", "bottom-end"] : ["top-end", "bottom-end"]
      const actionCell = toggle.closest("[data-column-key='actions']")
      const controllerElement = this.element

      this.fixedDropdownInstances.push(
        Dropdown.getOrCreateInstance(toggle, {
          boundary: "viewport",
          popperConfig(defaultConfig) {
            const defaultModifiers = defaultConfig.modifiers || []
            const hasOffsetModifier = defaultModifiers.some((modifier) => modifier.name === "offset")

            return {
              ...defaultConfig,
              placement,
              strategy: "fixed",
              modifiers: [
                ...defaultModifiers.map((modifier) => {
                  if (modifier.name === "offset") {
                    return {
                      ...modifier,
                      options: {
                        ...(modifier.options || {}),
                        offset: [0, FIXED_DROPDOWN_TRIGGER_GAP]
                      }
                    }
                  }

                  if (modifier.name === "flip") {
                    return {
                      ...modifier,
                      options: {
                        ...(modifier.options || {}),
                        boundary: "viewport",
                        rootBoundary: "viewport",
                        fallbackPlacements,
                        padding: FIXED_DROPDOWN_VIEWPORT_PADDING
                      }
                    }
                  }

                  if (modifier.name !== "preventOverflow") return modifier

                  return {
                    ...modifier,
                    options: {
                      ...(modifier.options || {}),
                      altAxis: true,
                      boundary: "viewport",
                      rootBoundary: "viewport",
                      padding: FIXED_DROPDOWN_VIEWPORT_PADDING,
                      tether: false
                    }
                  }
                }),
                ...(hasOffsetModifier
                  ? []
                  : [{ name: "offset", options: { offset: [0, FIXED_DROPDOWN_TRIGGER_GAP] } }]),
                {
                  name: "tableFixedDropdownGutter",
                  enabled: true,
                  phase: "afterWrite",
                  fn({ state }) {
                    const menu = state.elements.popper
                    if (!menu) return

                    const viewportWidth = document.documentElement.clientWidth
                    const viewportHeight = document.documentElement.clientHeight
                    const maxMenuHeight = Math.max(220, viewportHeight - FIXED_DROPDOWN_VIEWPORT_PADDING * 2)
                    menu.style.maxHeight = `${maxMenuHeight}px`
                    menu.style.overflowY = "auto"
                    menu.style.overscrollBehavior = "contain"

                    const menuRect = menu.getBoundingClientRect()
                    const controllerRect = controllerElement.getBoundingClientRect()
                    // Clear whatever renders FIRST in the actions cell, not the cell's reserved
                    // width — that width is padded to fit the widest row in the table (one also
                    // showing the delivery-complaint icon, see .documents-table-shell--wide-actions),
                    // so most rows don't actually use all of it, and reserving the whole column
                    // shoves the menu needlessly far left. On a plain row the cell's first child IS
                    // the toggle's own wrapper, so this lands exactly where Popper's left-start
                    // placement already puts the menu (a no-op). On a row with a leading sibling —
                    // the resend/acknowledge button or complaint glyph documents/emails/_delivery_action
                    // renders before the toggle — this is what keeps the open menu from covering it.
                    const leadingElement = actionCell?.firstElementChild ?? toggle
                    const rightBoundary = actionCell
                      ? Math.min(controllerRect.right, viewportWidth, leadingElement.getBoundingClientRect().left) -
                        FIXED_DROPDOWN_TRIGGER_GAP
                      : viewportWidth - FIXED_DROPDOWN_VIEWPORT_PADDING
                    const bottomBoundary = viewportHeight - FIXED_DROPDOWN_VIEWPORT_PADDING
                    let shiftX = 0
                    let shiftY = 0

                    if (menuRect.right > rightBoundary) {
                      shiftX = rightBoundary - menuRect.right
                    }
                    if (menuRect.left + shiftX < FIXED_DROPDOWN_VIEWPORT_PADDING) {
                      shiftX += FIXED_DROPDOWN_VIEWPORT_PADDING - (menuRect.left + shiftX)
                    }
                    if (menuRect.bottom > bottomBoundary) {
                      shiftY = bottomBoundary - menuRect.bottom
                    }
                    if (menuRect.top + shiftY < FIXED_DROPDOWN_VIEWPORT_PADDING) {
                      shiftY += FIXED_DROPDOWN_VIEWPORT_PADDING - (menuRect.top + shiftY)
                    }

                    const baseTransform = state.styles.popper?.transform || "translate3d(0, 0, 0)"
                    menu.style.transform =
                      shiftX === 0 && shiftY === 0
                        ? baseTransform
                        : `${baseTransform} translate3d(${Math.round(shiftX)}px, ${Math.round(shiftY)}px, 0)`
                  }
                }
              ]
            }
          }
        })
      )
    })
  }

  #fixedDropdownPlacement(toggle) {
    if (toggle.closest("[data-column-key='actions'], .is-sticky-column-right")) {
      return "left-start"
    }

    return "bottom-end"
  }

  #teardownFixedDropdowns() {
    this.sheetTopLayerCleanups?.forEach((cleanup) => cleanup())
    this.sheetTopLayerCleanups = []
    this.fixedDropdownInstances?.forEach((instance) => instance.dispose())
    this.fixedDropdownInstances = []
  }

  // `position: fixed` does not get a sheet out of the table on iOS Safari. The
  // menu's box is right (fixed, full width, bottom: 0 — measured on an iPhone
  // simulator), but WebKit PAINTS it clipped to the `.documents-table-scroll`
  // overflow scroller it sits in: the sheet shows as a strip inside the table,
  // the last items are cut off, and the dim never leaves the table either.
  // Chrome's responsive mode does not clip it, which is why this only showed on
  // a real phone. Only `overflow` on that scroller mattered; isolation,
  // position/z-index, overscroll-behavior and scrollbar-* made no difference.
  //
  // The top layer is the way out that costs nothing else. The menu stays where
  // it is in the DOM, so Bootstrap's sibling lookup, keyboard handling, Stimulus
  // scoping and Turbo's form submission are untouched, but it paints above
  // every ancestor's clip. The menu also becomes the dim — full viewport, scrim
  // background — because a popover's ::backdrop is `pointer-events: none` and
  // would let a dismissing tap fall through to the row beneath it.
  //
  // `popover="manual"` keeps Bootstrap the only thing that opens and closes the
  // menu — no light dismiss, no Escape handling of the browser's own. A browser
  // without the Popover API keeps the clipped-but-usable sheet it had before.
  #promoteSheetToTopLayer(toggle, menu) {
    if (!menu || typeof menu.showPopover !== "function") return

    menu.setAttribute("popover", "manual")
    const open = () => {
      if (!menu.matches(":popover-open")) menu.showPopover()
    }
    // `hide`, not `hidden`: Bootstrap drops `.show` between the two, which would
    // flash the menu back into the table for a frame.
    const close = () => {
      if (menu.matches(":popover-open")) menu.hidePopover()
    }
    toggle.addEventListener("shown.bs.dropdown", open)
    toggle.addEventListener("hide.bs.dropdown", close)
    // A re-stage while a sheet is open (a Turbo Stream replacing a row, which
    // tears these listeners down and rebuilds them) must not drop the still-open
    // menu out of the top layer: Bootstrap never fired `shown` for it again.
    if (menu.classList.contains("show")) open()

    this.sheetTopLayerCleanups ||= []
    this.sheetTopLayerCleanups.push(() => {
      toggle.removeEventListener("shown.bs.dropdown", open)
      toggle.removeEventListener("hide.bs.dropdown", close)
      close()
      menu.removeAttribute("popover")
    })
  }

  // Popper's own teardown resets the properties IT wrote (position, inset,
  // transform); the gutter modifier above writes three more directly on the
  // element, and those outlive a dispose. Rotating a phone into the sheet
  // breakpoint would otherwise leave the sheet capped at the height the
  // anchored menu had been clamped to.
  #clearFixedDropdownInlineStyles(menu) {
    const properties = [
      // Written by Popper's applyStyles modifier.
      "position",
      "top",
      "left",
      "right",
      "bottom",
      "margin",
      "transform",
      // Written by tableFixedDropdownGutter, which Popper knows nothing about.
      "max-height",
      "overflow-y",
      "overscroll-behavior"
    ]
    for (const property of properties) menu.style.removeProperty(property)
  }

  // Rotating a phone, or resizing a desktop window across the breakpoint,
  // has to restage every row menu: sheet and popover are different Bootstrap
  // configurations, and `getOrCreateInstance` returns the existing instance
  // without applying a new one, so the switch has to go through dispose.
  #watchCompactViewport() {
    this.compactViewportQuery = window.matchMedia?.(COMPACT_VIEWPORT_QUERY)
    // A MediaQueryList that cannot be subscribed to still answers `matches`, so
    // the sheet/popover choice made at connect is correct either way — only the
    // restaging on rotation is lost. Reached by pre-14 Safari, which exposes
    // addListener alone, and by a test double that returns a bare object.
    if (typeof this.compactViewportQuery?.addEventListener !== "function") {
      this.compactViewportQuery = null
      return
    }

    this.boundHandleCompactViewportChange = () => {
      // Close first. Disposing an instance whose menu is open leaves the menu
      // with `.show` and nothing left to close it — a sheet stranded over the
      // table until the next full render.
      this.fixedDropdownInstances?.forEach((instance) => instance.hide?.())
      this.#teardownFixedDropdowns()
      this.#setupFixedDropdowns()
    }
    this.compactViewportQuery.addEventListener("change", this.boundHandleCompactViewportChange)
  }

  #unwatchCompactViewport() {
    if (!this.compactViewportQuery || !this.boundHandleCompactViewportChange) return

    this.compactViewportQuery.removeEventListener("change", this.boundHandleCompactViewportChange)
    this.compactViewportQuery = null
    this.boundHandleCompactViewportChange = null
  }

  #updateScrollState() {
    if (!this.hasTableTarget) return

    this.#syncColumnWidths()
    this.#applyStickyColumns()
    // #handleTableScroll ends with #positionViewSettingsMenu, and nothing here
    // mutates the DOM after it, so a second call would only re-read the anchor.
    this.#handleTableScroll()
  }

  #handleTableScroll() {
    if (!this.hasTableTarget) return

    this.#updateStickyHeaderState()
    this.#updateHorizontalScrollState()
    this.#positionViewSettingsMenu()
  }

  #updateStickyHeaderState() {
    if (!(this.stickyHeaderValue && this.verticalScrollContainer)) return

    this.element.classList.toggle("is-table-header-stuck", this.verticalScrollContainer.scrollTop > 0)
    this.#syncHeaderScroll()
  }

  #syncHeaderScroll() {
    if (!(this.stickyHeaderValue && this.hasHeaderViewportTarget && this.verticalScrollContainer)) return

    this.headerViewportTarget.scrollLeft = this.verticalScrollContainer.scrollLeft
  }

  #updateHorizontalScrollState() {
    if (!(this.stickyColumnsValue && this.verticalScrollContainer)) return

    const containerWidth =
      this.verticalScrollContainer.clientWidth || Number.parseFloat(this.verticalScrollContainer.style.width) || 0
    const contentWidth =
      this.verticalScrollContainer.scrollWidth ||
      this.tableTarget.getBoundingClientRect().width ||
      Number.parseFloat(this.tableTarget.style.width) ||
      0
    const maxScrollLeft = Math.max(0, contentWidth - containerWidth)
    const scrollLeft = Math.max(0, this.verticalScrollContainer.scrollLeft)
    const isScrollable = maxScrollLeft > 1

    this.element.classList.toggle("is-table-scrollable-x", isScrollable)
    this.element.classList.toggle("is-table-scrolled-x", isScrollable && scrollLeft > 1)
    this.element.classList.toggle("is-table-scrolled-x-end", !isScrollable || scrollLeft >= maxScrollLeft - 1)
  }

  #loadState() {
    return this.columnStateStore.load()
  }

  #persistState() {
    this.state = this.columnStateStore.persist(this.state)

    document.dispatchEvent(
      new CustomEvent("lucanto:table-columns-changed", {
        detail: { tableElement: this.element, state: this.exportColumnState() }
      })
    )
  }

  #applyState() {
    // Order, visibility and the widths below all change what the browser has to
    // fit into the row, so whatever was measured before no longer describes it.
    this.columnWidthsDirty = true
    this.#applyOrder()
    this.#applyVisibility()
    this.#applyWidths()
    if (this.columnResizeValue) {
      this.#setupResizers()
    } else {
      this.#teardownResizers()
    }
  }

  #applyOrder() {
    const orderedKeys = this.#normalizedOrder()
    const allRows = [
      this.headerRowTarget,
      ...this.bodyTargets.flatMap((body) => Array.from(body.querySelectorAll("tr")))
    ]

    for (const row of allRows) {
      const cellsByKey = new Map()

      Array.from(row.children).forEach((cell) => {
        const key = cell.dataset.columnKey
        if (key) {
          cellsByKey.set(key, cell)
        }
      })

      orderedKeys.forEach((key) => {
        const cell = cellsByKey.get(key)
        if (cell) row.appendChild(cell)
      })
    }
  }

  #applyVisibility() {
    if (!this.columnVisibilityValue) return
    const hiddenKeys = new Set(this.state.hidden)
    this.element.querySelectorAll("th[data-column-key], td[data-column-key]").forEach((cell) => {
      cell.classList.toggle("is-column-hidden", hiddenKeys.has(cell.dataset.columnKey))
    })
  }

  #applyWidths() {
    if (!this.columnResizeValue) return
    this.columns.forEach((column) => {
      const width = this.state.widths[column.key]
      this.element
        .querySelectorAll(`th[data-column-key="${column.key}"], td[data-column-key="${column.key}"]`)
        .forEach((cell) => {
          if (width) {
            this.#setCellWidth(cell, width)
          } else {
            this.#clearCellWidth(cell)
          }
        })
    })
  }

  #setupResizers() {
    if (!this.columnResizeValue) return
    this.#teardownResizers()
    this.resizerCleanups = []

    // Hoisted out of the loop: neither depends on the header cell, and
    // #visibleColumnKeys costs a getComputedStyle per column — inside the loop
    // that is N² style reads to build N handles, on a path that runs on connect
    // and on every fragment reconcile, column add, remove and reorder.
    const visibleKeys = this.#visibleColumnKeys()
    const firstStickyDataKey = this.#leftStickyColumnKeys(visibleKeys).find((key) => key !== "select")

    this.headerRowTarget.querySelectorAll("th[data-column-key]").forEach((headerCell) => {
      if (headerCell.dataset.columnFixed === "true") return

      const handle = document.createElement("button")
      handle.type = "button"
      handle.className = "lc-table__column-resizer"
      const columnKey = headerCell.dataset.columnKey
      const column = this.columns.find((item) => item.key === columnKey)
      const keyboardMinimumWidth = Math.max(COLUMN_RESIZE_MIN_WIDTH, this.#minimumColumnWidth(columnKey, firstStickyDataKey))
      const currentWidth = Math.min(
        MAX_COLUMN_WIDTH,
        Math.max(keyboardMinimumWidth, Math.round(Number(this.state.widths[columnKey]) || this.#columnWidth(columnKey)))
      )
      handle.setAttribute("aria-label", `${this.#texts.resizeColumnLabel}: ${column?.label || columnKey}`)
      handle.setAttribute("role", "separator")
      handle.setAttribute("aria-orientation", "vertical")
      handle.setAttribute("aria-valuemin", keyboardMinimumWidth.toString())
      handle.setAttribute("aria-valuemax", MAX_COLUMN_WIDTH.toString())
      handle.setAttribute("aria-valuenow", currentWidth.toString())
      handle.setAttribute("aria-keyshortcuts", "ArrowLeft ArrowRight")
      handle.dataset.tableInteractionsIgnore = "true"

      const resizeWithKeyboard = (keyEvent) => {
        if (!["ArrowLeft", "ArrowRight"].includes(keyEvent.key)) return
        keyEvent.preventDefault()
        keyEvent.stopPropagation()
        const scrollLeft = this.#currentHorizontalScrollLeft()
        const step = keyEvent.shiftKey ? 25 : 10
        const direction = keyEvent.key === "ArrowLeft" ? -1 : 1
        const width = Math.min(
          MAX_COLUMN_WIDTH,
          Math.max(
            keyboardMinimumWidth,
            (Number(this.state.widths[columnKey]) || this.#columnWidth(columnKey) || keyboardMinimumWidth) +
              step * direction
          )
        )
        this.state.widths[columnKey] = Math.round(width)
        handle.setAttribute("aria-valuenow", Math.round(width).toString())
        this.#applyWidths()
        this.#persistState()
        this.#updateScrollState()
        this.#restoreHorizontalScrollPosition(scrollLeft)
      }

      const move = (moveEvent) => {
        if (!this.activeColumnResize) return

        this.#captureResizeScrollPosition()
        const width = Math.min(
          MAX_COLUMN_WIDTH,
          Math.max(
            this.activeColumnResize.minimumWidth,
            this.activeColumnResize.startWidth + (moveEvent.clientX - this.activeColumnResize.startClientX)
          )
        )
        this.state.widths[this.activeColumnResize.columnKey] = Math.round(width)
        handle.setAttribute("aria-valuenow", Math.round(width).toString())
        this.#applyWidths()
        this.#updateScrollState()
        this.#restoreResizeScrollPosition()
        this.#updateHorizontalScrollState()
      }

      const stop = (stopEvent) => {
        window.removeEventListener("pointermove", move)
        window.removeEventListener("pointerup", stop)
        window.removeEventListener("pointercancel", stop)
        window.removeEventListener("mousemove", move)
        window.removeEventListener("mouseup", stop)
        if (stopEvent?.pointerId != null) {
          handle.releasePointerCapture?.(stopEvent.pointerId)
        }
        const scrollLeft = this.#currentHorizontalScrollLeft()
        document.body.classList.remove("is-resizing-table-column")
        headerCell.classList.remove("is-column-resizing")
        handle.classList.remove("is-active")
        this.#persistState()
        this.activeColumnResize = null
        // Re-decide the pin against the width just released; the drag held it.
        // #applyStickyColumns is its only consumer, so a full #updateScrollState
        // here would re-measure every column to recompute what `move` already
        // wrote. Paired with a restore like its two sibling paths, since this is
        // the pass that can unpin a column and move the row under the cursor.
        this.#applyStickyColumns()
        this.#restoreHorizontalScrollPosition(scrollLeft)
      }

      const start = (startEvent) => {
        if (startEvent.pointerType && startEvent.isPrimary === false) return
        startEvent.preventDefault()
        startEvent.stopPropagation()
        const columnKey = headerCell.dataset.columnKey
        this.activeColumnResize = {
          columnKey,
          // Frozen for the length of the drag — see #affordableLeftStickyColumnKeys.
          leftStickyKeys: this.#affordableLeftStickyColumnKeys(this.#visibleColumnKeys()),
          minimumWidth: keyboardMinimumWidth,
          scrollLeft: this.#currentHorizontalScrollLeft(),
          startClientX: startEvent.clientX,
          startWidth: Number(this.state.widths[columnKey]) || this.#columnWidth(columnKey)
        }
        document.body.classList.add("is-resizing-table-column")
        headerCell.classList.add("is-column-resizing")
        handle.classList.add("is-active")
        if (startEvent.pointerId != null) {
          handle.setPointerCapture?.(startEvent.pointerId)
          window.addEventListener("pointermove", move)
          window.addEventListener("pointerup", stop)
          window.addEventListener("pointercancel", stop)
        } else {
          window.addEventListener("mousemove", move)
          window.addEventListener("mouseup", stop)
        }
      }

      if ("PointerEvent" in window) {
        handle.addEventListener("pointerdown", start)
      } else {
        handle.addEventListener("mousedown", start)
      }
      handle.addEventListener("keydown", resizeWithKeyboard)
      headerCell.appendChild(handle)

      this.resizerCleanups.push(() => {
        handle.removeEventListener("pointerdown", start)
        handle.removeEventListener("mousedown", start)
        handle.removeEventListener("keydown", resizeWithKeyboard)
        handle.remove()
      })
    })
  }

  #teardownResizers() {
    this.resizerCleanups?.forEach((cleanup) => cleanup())
    this.resizerCleanups = []
    this.activeColumnResize = null
  }

  #currentHorizontalScrollLeft() {
    const scrollContainer = this.#horizontalScrollContainer()
    if (scrollContainer) return Math.max(0, scrollContainer.scrollLeft)
    if (this.hasHeaderViewportTarget) return Math.max(0, this.headerViewportTarget.scrollLeft)

    return 0
  }

  #captureResizeScrollPosition() {
    if (!this.activeColumnResize) return

    this.activeColumnResize.scrollLeft = this.#currentHorizontalScrollLeft()
  }

  #restoreResizeScrollPosition() {
    if (!this.activeColumnResize) return

    this.#restoreHorizontalScrollPosition(this.activeColumnResize.scrollLeft)
  }

  #restoreHorizontalScrollPosition(scrollLeft) {
    const clampedScrollLeft = this.#clampedHorizontalScrollLeft(scrollLeft)
    const scrollContainer = this.#horizontalScrollContainer()
    if (scrollContainer) scrollContainer.scrollLeft = clampedScrollLeft
    if (this.hasHeaderViewportTarget) this.headerViewportTarget.scrollLeft = clampedScrollLeft
  }

  #clampedHorizontalScrollLeft(scrollLeft) {
    const scrollContainer = this.#horizontalScrollContainer()
    if (!scrollContainer) return Math.max(0, scrollLeft)

    const contentWidth =
      scrollContainer.scrollWidth ||
      (this.hasTableTarget ? this.tableTarget.getBoundingClientRect().width : 0) ||
      (this.hasTableTarget ? Number.parseFloat(this.tableTarget.style.width) : 0) ||
      0
    const containerWidth = scrollContainer.clientWidth || Number.parseFloat(scrollContainer.style.width) || 0
    const maxScrollLeft = Math.max(0, contentWidth - containerWidth)

    return Math.min(Math.max(0, scrollLeft), maxScrollLeft)
  }

  #horizontalScrollContainer() {
    return this.verticalScrollContainer || this.element.querySelector(".documents-table-scroll")
  }

  #setupHeaderSorting() {
    if (!this.columnReorderValue) return
    this.#destroyHeaderSortable()
    this.headerSortable = Sortable.create(this.headerRowTarget, {
      animation: 180,
      draggable: "th[data-column-key]:not([data-column-fixed='true'])",
      handle: ".lc-table__column-drag-handle",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      onEnd: () => {
        this.state.order = Array.from(this.headerRowTarget.querySelectorAll("th[data-column-key]")).map(
          (cell) => cell.dataset.columnKey
        )
        this.#persistState()
        this.#applyOrder()
        this.#renderColumnMenus()
        this.#updateScrollState()
      }
    })
  }

  #destroyHeaderSortable() {
    const sortable = this.headerSortable
    this.headerSortable = null
    sortable?.destroy()
  }

  #setupDragHandles() {
    this.#teardownDragHandles()
    this.dragHandleCleanups = []

    this.headerRowTarget.querySelectorAll("th[data-column-key]").forEach((headerCell) => {
      if (headerCell.dataset.columnFixed === "true") return
      if (headerCell.querySelector(".lc-table__column-drag-handle")) return

      const handle = document.createElement("button")
      handle.type = "button"
      handle.className = "lc-table__column-drag-handle"
      const columnKey = headerCell.dataset.columnKey
      const column = this.columns.find((item) => item.key === columnKey)
      handle.setAttribute("aria-label", `${this.#texts.reorderColumnLabel}: ${column?.label || columnKey}`)
      handle.setAttribute("aria-keyshortcuts", "ArrowLeft ArrowRight")
      handle.dataset.action = "keydown->table-interactions#reorderColumnWithKeyboard"
      handle.dataset.columnControlKey = columnKey
      handle.dataset.tableInteractionsIgnore = "true"
      handle.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">drag_indicator</span>'

      const swallowClick = (event) => {
        event.preventDefault()
        event.stopPropagation()
      }

      handle.addEventListener("click", swallowClick)

      headerCell.appendChild(handle)
      this.dragHandleCleanups.push(() => {
        handle.removeEventListener("click", swallowClick)
        handle.remove()
      })
    })
  }

  #teardownDragHandles() {
    this.dragHandleCleanups?.forEach((cleanup) => cleanup())
    this.dragHandleCleanups = []
  }

  #renderColumnMenus() {
    if (!(this.columnVisibilityValue || this.columnReorderValue)) return
    if (!this.hasColumnsListTarget) return

    this.columnsListTarget.innerHTML = ""

    this.#normalizedOrder().forEach((key) => {
      const column = this.columns.find((item) => item.key === key)
      if (!column || this.state.hidden.includes(key)) return

      const item = document.createElement("div")
      item.className = `lc-table-view-settings__item${column.fixed ? " is-fixed" : ""}`
      item.dataset.id = key
      item.innerHTML = `
        ${
          column.fixed
            ? `<span class="lc-table-view-settings__lock" aria-hidden="true">
                <span class="material-symbols-outlined">lock</span>
              </span>`
            : `<button type="button" class="lc-table-view-settings__drag" data-action="keydown->table-interactions#reorderColumnWithKeyboard" data-column-control-key="${column.key}" data-table-interactions-ignore="true" aria-keyshortcuts="ArrowLeft ArrowRight" aria-label="${this.#escapeAttribute(`${this.#texts.reorderColumnLabel}: ${column.label}`)}">
                <span class="material-symbols-outlined" aria-hidden="true">drag_indicator</span>
              </button>`
        }
        <span class="lc-table-view-settings__label">${column.label}</span>
        ${
          this.columnVisibilityValue && column.removable && !column.fixed
            ? `<button type="button" class="lc-table-view-settings__remove" data-controller="tooltip" data-tooltip-title-value="${this.#escapeAttribute(this.#texts.removeColumnLabel)}" data-tooltip-class-value="tooltip--dark" data-tooltip-placement-value="top" data-action="table-interactions#removeColumn" data-column-remove-key="${column.key}" data-table-interactions-ignore="true" aria-label="${this.#escapeAttribute(this.#texts.removeColumnLabel)}">
                <span class="material-symbols-outlined">close</span>
              </button>`
            : `<span class="lc-table-view-settings__fixed-placeholder" aria-hidden="true"></span>`
        }
      `
      this.columnsListTarget.appendChild(item)
    })

    this.#destroyMenuSortable()
    if (this.columnReorderValue) {
      this.menuSortable = Sortable.create(this.columnsListTarget, {
        animation: 180,
        handle: ".lc-table-view-settings__drag",
        ghostClass: "sortable-ghost",
        chosenClass: "sortable-chosen",
        dragClass: "sortable-drag",
        onEnd: () => {
          const orderedVisibleKeys = Array.from(this.columnsListTarget.children).map((item) => item.dataset.id)
          const hiddenKeys = this.state.hidden.filter((key) => !orderedVisibleKeys.includes(key))
          this.state.order = [...orderedVisibleKeys, ...hiddenKeys]
          this.#persistState()
          this.#applyOrder()
          this.#renderAddColumnMenu()
          this.#updateScrollState()
        }
      })
    }
  }

  #destroyMenuSortable() {
    const sortable = this.menuSortable
    this.menuSortable = null
    sortable?.destroy()
  }

  #applyStickyColumns() {
    if (!this.hasHeaderRowTarget) return

    this.#clearStickyColumns()
    if (!(this.stickyColumnsValue && this.verticalScrollContainer)) return

    const visibleKeys = this.#visibleColumnKeys()
    const leftKeys = this.#affordableLeftStickyColumnKeys(visibleKeys)
    const rightKeys = this.#rightStickyColumnKeys(visibleKeys)

    let leftOffset = 0
    leftKeys.forEach((key, index) => {
      this.#cellsForKey(key).forEach((cell) => {
        cell.classList.add("is-sticky-column", "is-sticky-column-left")
        cell.classList.toggle("is-sticky-column-left-edge", index === leftKeys.length - 1)
        cell.style.left = `${leftOffset}px`
      })
      leftOffset += this.#columnWidth(key)
    })

    let rightOffset = 0
    const verticalScrollbarGutter = this.#verticalScrollbarGutter()
    if (this.hasHeaderViewportTarget) {
      this.headerViewportTarget.style.setProperty(
        "--lc-table-vertical-scrollbar-gutter",
        `${verticalScrollbarGutter}px`
      )
    }
    const orderedRightKeys = [...rightKeys].reverse()
    orderedRightKeys.forEach((key, index) => {
      this.#cellsForKey(key).forEach((cell) => {
        cell.classList.add("is-sticky-column", "is-sticky-column-right")
        cell.classList.toggle("is-sticky-column-right-edge", index === rightKeys.length - 1)
        const headerGutter = cell.closest(".documents-table-header") ? verticalScrollbarGutter : 0
        cell.style.right = `${rightOffset + headerGutter}px`
      })
      rightOffset += this.#columnWidth(key)
    })
  }

  #verticalScrollbarGutter() {
    if (!this.verticalScrollContainer) return 0

    const offsetWidth = this.verticalScrollContainer.offsetWidth || 0
    const clientWidth = this.verticalScrollContainer.clientWidth || 0
    if (!(offsetWidth > 0 && clientWidth > 0)) return 0

    const styles = window.getComputedStyle(this.verticalScrollContainer)
    const borderWidth =
      (Number.parseFloat(styles.borderLeftWidth) || 0) + (Number.parseFloat(styles.borderRightWidth) || 0)

    return Math.max(0, Math.round(offsetWidth - clientWidth - borderWidth))
  }

  #syncColumnWidths() {
    if (!(this.stickyColumnsValue && this.hasHeaderRowTarget && this.hasBodyTarget)) return

    const visibleKeys = this.#visibleColumnKeys()
    const firstStickyDataKey = this.#leftStickyColumnKeys(visibleKeys).find((key) => key !== "select")

    this.#refreshAutoWidthScope()
    if (this.columnWidthsDirty) this.#restartColumnMeasurement(visibleKeys)

    visibleKeys.forEach((key) => {
      // Looked up once and handed down. #cellsForKey is a querySelectorAll over
      // the whole table plus a getComputedStyle per cell, so calling it to
      // measure and again to write cost ~100 style reads per column on a 50-row
      // table — on a pass that also runs on every pointermove of a resize drag.
      const cells = this.#cellsForKey(key)
      const width = this.#syncedColumnWidth(key, this.#minimumColumnWidth(key, firstStickyDataKey), cells)
      if (!width) return

      cells.forEach((cell) => {
        this.#setCellWidth(cell, width)
      })
    })
  }

  // Hands the columns back to the browser before measuring them, so the loop
  // above reads the layout the browser picks for the row as it is now. Measuring
  // cells that still carry our previous pins reads those pins straight back,
  // which is how a width from one layout used to survive into every later one.
  #restartColumnMeasurement(visibleKeys) {
    this.columnWidthsDirty = false
    // Nothing to re-derive a width from — keep the ones the columns already
    // carry rather than throwing them away (a detached or hidden table).
    if (!this.#columnGeometry()) return

    visibleKeys
      .filter((key) => !FIXED_UTILITY_COLUMN_KEYS.includes(key) && !this.#persistedColumnWidth(key))
      .forEach((key) =>
        this.#cellsForKey(key).forEach((cell) => {
          this.#clearCellWidth(cell)
        })
      )

    // Unpinned, a table with room to spare is stretched to fill it by
    // `min-width: 100%`, so every width about to be measured is this layout's
    // share of the spare room rather than the column's own width. Pin those —
    // both tables stretch alike, which is what keeps them aligned — but never
    // remember them.
    const geometry = this.#columnGeometry()
    this.columnWidthsCacheable = Boolean(geometry) && geometry.tableWidth > geometry.containerWidth + 1
  }

  #columnGeometry() {
    const containerWidth = this.verticalScrollContainer?.clientWidth || 0
    const tableWidth = this.hasTableTarget ? this.tableTarget.getBoundingClientRect().width || 0 : 0
    if (!(containerWidth > 0 && tableWidth > 0)) return null

    return { containerWidth, tableWidth }
  }

  #persistedColumnWidth(key) {
    const width = Number(this.state.widths[key])

    return Number.isFinite(width) && width > 0 ? width : 0
  }

  #syncedColumnWidth(key, minimumWidth, cells) {
    // minimumWidth already IS the fixed width for these two — #minimumColumnWidth
    // opens with the same #fixedUtilityColumnWidth call — so resolving it a
    // second time only pays for #actionsColumnWidth's getComputedStyle again.
    if (FIXED_UTILITY_COLUMN_KEYS.includes(key)) return Math.ceil(minimumWidth)

    const persistedWidth = this.#persistedColumnWidth(key)
    if (persistedWidth) return persistedWidth

    const cachedWidth = Number(this.autoWidths[key]) || 0
    if (cells.length === 0) return Math.max(cachedWidth, minimumWidth)

    const measuredWidth = Math.max(
      ...cells.map((cell) => {
        const inlineWidth = Number.parseFloat(cell.style.width) || 0
        const rectWidth = cell.getBoundingClientRect().width || 0
        const offsetWidth = cell.offsetWidth || 0

        return Math.max(inlineWidth, rectWidth, offsetWidth)
      })
    )

    const width = Math.ceil(Math.max(measuredWidth, cachedWidth, minimumWidth))
    if (this.columnWidthsCacheable) this.#rememberAutoWidth(key, width)

    return width
  }

  #minimumColumnWidth(key, firstStickyDataKey) {
    const fixedWidth = this.#fixedUtilityColumnWidth(key)
    if (fixedWidth) return fixedWidth
    if (key === firstStickyDataKey)
      return isCompactViewport() ? FIRST_STICKY_DATA_COLUMN_MOBILE_MIN_WIDTH : FIRST_STICKY_DATA_COLUMN_MIN_WIDTH

    return 0
  }

  #fixedUtilityColumnWidth(key) {
    if (key === "select") return SELECTION_COLUMN_WIDTH
    if (key === "actions") return this.#actionsColumnWidth()

    return 0
  }

  // Read off an actions CELL, never off this.element. The custom property is
  // declared on `.documents-table-shell` and the controller element is its
  // PARENT (`.documents-table-controller`, documents/_index.html.erb) — which
  // carries the shared 5.2rem default. Reading there would quietly return the
  // narrow value on a --wide-actions table and look like it was working. The
  // cells are where the property lands after inheritance and where the
  // `width: var(--lc-table-actions-column-width, 5.2rem)` rule resolves it, so
  // they are the only place guaranteed to agree with what the browser paints.
  //
  // Cost: this is called from BOTH #minimumColumnWidth and #syncedColumnWidth
  // for the `actions` key, and each call also runs #rootFontSize(), so a column
  // sync pass does four getComputedStyle reads here, not the one an earlier
  // version of this comment claimed. That is fine — the pass is bound to
  // `resize` and ResizeObserver, never to scroll, and #syncColumnWidths already
  // reads the scroll container while the non-fixed branch below measures every
  // cell. Recorded accurately because the number will be trusted by whoever
  // profiles this next.
  #actionsColumnWidth() {
    const cell = this.element.querySelector('th[data-column-key="actions"], td[data-column-key="actions"]')
    if (!cell) return ACTIONS_COLUMN_WIDTH_FALLBACK

    const declared = window.getComputedStyle(cell).getPropertyValue("--lc-table-actions-column-width")

    return this.#cssLengthToPixels(declared) || ACTIONS_COLUMN_WIDTH_FALLBACK
  }

  // Custom properties are NOT resolved to pixels by getComputedStyle — the
  // declared token comes back verbatim ("11.8rem"), so the conversion is ours.
  // Via the root font size rather than a hardcoded 10, because that is where
  // `$font-size-root: 62.5%` actually lands, and it moves with browser zoom and
  // a user's minimum-font-size setting.
  //
  // Only `px` and `rem` are understood, which is every value the stylesheets
  // declare today. Two different things fail this, and they need opposite
  // treatment:
  //
  //   NOT DECLARED — `getPropertyValue` returns "". The nine tables that set no
  //   width of their own are in this state on every sync pass, and the
  //   `var(…, 5.2rem)` fallback in the stylesheet is the right answer for them.
  //   Silent, by design.
  //
  //   DECLARED AND UNCONVERTIBLE — `8em`, `50%`, a `calc()`, a `clamp()`, `0px`,
  //   a negative. Somebody wrote a width this converter cannot read, and the
  //   failure is INVISIBLE without the warning: the browser paints the real
  //   width while the sticky right-offset arithmetic works from 52px, so the
  //   columns drift with nothing in the console to explain it. An earlier
  //   version put the `Number.isFinite` guard first and returned before the
  //   warning, so `calc()` and `clamp()` — two of the four inputs this comment
  //   names — took the silent path anyway.
  //
  // The warning is emitted once per distinct declared value per controller
  // instance: #actionsColumnWidth is called twice per sync pass and the pass is
  // bound to `resize`, so an un-deduplicated warning floods the console while a
  // window is being dragged, which buries the one line that mattered.
  #cssLengthToPixels(value) {
    const declared = (value || "").trim()
    if (declared === "") return 0

    const number = Number.parseFloat(declared)
    if (Number.isFinite(number) && number > 0) {
      if (declared.endsWith("px")) return number
      if (declared.endsWith("rem")) return number * this.#rootFontSize()
    }

    this.#warnUnconvertibleActionsWidth(declared)

    return 0
  }

  #warnUnconvertibleActionsWidth(declared) {
    if (this.warnedActionsWidths.has(declared)) return

    this.warnedActionsWidths.add(declared)
    console.warn(
      `[table-interactions] unsupported length "${declared}" for --lc-table-actions-column-width; ` +
        `falling back to ${ACTIONS_COLUMN_WIDTH_FALLBACK}px. Only positive px and rem are converted.`
    )
  }

  #rootFontSize() {
    const size = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize)

    return Number.isFinite(size) && size > 0 ? size : 16
  }

  #positionViewSettingsMenu() {
    if (!this.hasViewSettingsMenuTarget || this.viewSettingsMenuTarget.hidden) return

    const anchor =
      this.activeViewSettingsButton ||
      this.viewSettingsButtonTargets.find((button) => button.classList.contains("is-active")) ||
      (this.hasViewSettingsButtonTarget ? this.viewSettingsButtonTarget : null)
    if (!anchor) return

    const spacing = 8
    const anchorRect = anchor.getBoundingClientRect()
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const menu = this.viewSettingsMenuTarget
    const rootFontSize = this.#rootFontSize()
    const desiredMaxHeight = 42 * rootFontSize
    const minimumUsefulHeight = Math.min(24 * rootFontSize, desiredMaxHeight)
    const availableBelow = Math.max(0, viewportHeight - anchorRect.bottom - spacing * 2)
    const availableAbove = Math.max(0, anchorRect.top - spacing * 2)
    const openBelow =
      availableBelow >= Math.min(minimumUsefulHeight, desiredMaxHeight) || availableBelow >= availableAbove
    const availableHeight = openBelow ? availableBelow : availableAbove
    const maxHeight = Math.max(0, Math.min(desiredMaxHeight, availableHeight))
    const top = openBelow
      ? Math.max(spacing, Math.floor(anchorRect.bottom + spacing))
      : Math.max(spacing, Math.floor(anchorRect.top - spacing - maxHeight))

    menu.style.position = "fixed"
    menu.style.top = `${top}px`
    menu.style.maxHeight = `${maxHeight}px`
    menu.style.zIndex = "1004"
    menu.dataset.placement = openBelow ? "bottom" : "top"

    if (isCompactViewport()) {
      menu.style.left = `${spacing}px`
      menu.style.right = `${spacing}px`
      menu.style.width = "auto"
      return
    }

    const desiredWidth = Math.min(31 * rootFontSize, viewportWidth - spacing * 2)
    const left = Math.min(
      Math.max(spacing, anchorRect.right - desiredWidth),
      Math.max(spacing, viewportWidth - desiredWidth - spacing)
    )
    menu.style.left = `${Math.floor(left)}px`
    menu.style.right = "auto"
    menu.style.width = `${Math.floor(desiredWidth)}px`
  }

  #clearViewSettingsMenuPosition() {
    if (!this.hasViewSettingsMenuTarget) return

    for (const property of VIEW_SETTINGS_POSITION_PROPERTIES) {
      this.viewSettingsMenuTarget.style.removeProperty(property)
    }
    delete this.viewSettingsMenuTarget.dataset.placement
  }

  #clearCellWidth(cell) {
    cell.style.removeProperty("width")
    cell.style.removeProperty("min-width")
    cell.style.removeProperty("max-width")
  }

  #setCellWidth(cell, width) {
    const widthValue = `${width}px`
    if (cell.style.width === widthValue && cell.style.minWidth === widthValue && cell.style.maxWidth === widthValue) {
      return
    }

    cell.style.width = widthValue
    cell.style.minWidth = widthValue
    cell.style.maxWidth = widthValue
  }

  #clearStickyColumns() {
    if (this.hasHeaderViewportTarget) {
      this.headerViewportTarget.style.removeProperty("--lc-table-vertical-scrollbar-gutter")
    }

    this.element.querySelectorAll("th.is-sticky-column, td.is-sticky-column").forEach((cell) => {
      cell.classList.remove(
        "is-sticky-column",
        "is-sticky-column-left",
        "is-sticky-column-left-edge",
        "is-sticky-column-right",
        "is-sticky-column-right-edge"
      )
      cell.style.removeProperty("left")
      cell.style.removeProperty("right")
    })
  }

  #visibleColumnKeys() {
    return this.#normalizedOrder().filter((key) => {
      const headerCell = this.headerRowTarget.querySelector(`[data-column-key="${key}"]`)
      return headerCell && this.#isCellVisible(headerCell)
    })
  }

  #leftStickyColumnKeys(visibleKeys) {
    const selectionKey = visibleKeys.includes("select") ? "select" : null
    const firstDataKey = visibleKeys.find((key) => key !== "select" && key !== "actions")

    return [selectionKey, firstDataKey].filter(Boolean)
  }

  // The candidates above, minus the first data column when pinning it would
  // leave less than STICKY_COLUMNS_MIN_SCROLL_WINDOW to scroll the columns it is
  // pinned against. Applied here and not in #leftStickyColumnKeys on purpose:
  // that one also answers "which column gets the first-sticky-data minimum
  // width" for #syncColumnWidths, which runs BEFORE this and is what these
  // widths are read from. Deciding there would make the widths depend on a
  // decision made from the widths.
  //
  // What is held for the length of a pointer drag is the pinned SET, not the
  // pinned geometry — the offsets below still track the width moving under the
  // user's hand, but the cell they are dragging must not jump between its
  // pinned offset and its in-flow position (out of view entirely on a table
  // scrolled sideways) at the moment it crosses the threshold, and back again
  // if they drag the other way. `resizeWithKeyboard` needs no such hold: an
  // arrow key is a committed step, not a gesture in progress, so re-deciding on
  // it is the layout answering the keypress.
  #affordableLeftStickyColumnKeys(visibleKeys) {
    if (this.activeColumnResize?.leftStickyKeys) return this.activeColumnResize.leftStickyKeys

    const candidates = this.#leftStickyColumnKeys(visibleKeys)

    // On a phone the identifier column never earns its pin, whatever it
    // measures. The free-window budget below let it stay pinned on a document
    // list — a 361px scroller (the invoice table's 1.6rem side margins) less
    // the 56px selection column and the usual 5.2rem actions column leaves
    // 253px, so an invoice-number column narrower than 133px cleared the
    // budget. It "fits", and the list still reads as unscrollable: 120 of the
    // 361 go to a column that never moves, and the reader drags a 133px window
    // across a table several times that wide. The row's identity is already
    // pinned beside it — the selection cell carries the row number — so on a
    // compact viewport the content column scrolls with the rest and the budget
    // below governs the roomy viewports it was written for.
    if (isCompactViewport()) return candidates.filter((key) => key === "select")

    const containerWidth = this.#measurementContainerWidth()
    if (containerWidth <= 0) return candidates

    const pinned = [...candidates, ...this.#rightStickyColumnKeys(visibleKeys)]
    const pinnedWidth = pinned.reduce((total, key) => total + this.#columnWidth(key), 0)
    if (containerWidth - pinnedWidth >= STICKY_COLUMNS_MIN_SCROLL_WINDOW) return candidates

    return candidates.filter((key) => key === "select")
  }

  #rightStickyColumnKeys(visibleKeys) {
    return visibleKeys.includes("actions") ? ["actions"] : []
  }

  #isCellVisible(cell) {
    return !cell.classList.contains("is-column-hidden") && window.getComputedStyle(cell).display !== "none"
  }

  #cellsForKey(key) {
    return Array.from(
      this.element.querySelectorAll(`th[data-column-key="${key}"], td[data-column-key="${key}"]`)
    ).filter((cell) => this.#isCellVisible(cell))
  }

  #columnWidth(key) {
    const headerCell = this.headerRowTarget.querySelector(`[data-column-key="${key}"]`)
    if (!headerCell) return 0

    const inlineWidth = Number.parseFloat(headerCell.style.width)
    return (
      (Number.isFinite(inlineWidth) && inlineWidth) ||
      headerCell.getBoundingClientRect().width ||
      headerCell.offsetWidth ||
      0
    )
  }

  #renderAddColumnMenu() {
    if (!this.columnVisibilityValue) return
    if (!this.hasAddColumnListTarget) return

    const hiddenColumns = this.columns.filter(
      (column) => this.state.hidden.includes(column.key) && column.removable && !column.fixed
    )

    this.addColumnListTarget.innerHTML = ""
    hiddenColumns.forEach((column) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "lc-table-view-settings__add-option"
      button.dataset.action = "table-interactions#addColumn"
      button.dataset.columnKey = column.key
      button.dataset.tableInteractionsIgnore = "true"
      button.dataset.searchText = this.#normalizeColumnSearch(column.label)
      button.textContent = column.label
      this.addColumnListTarget.appendChild(button)
    })
    this.#filterAddColumnOptions(this.hasAddColumnSearchTarget ? this.addColumnSearchTarget.value : "")
  }

  #filterAddColumnOptions(query) {
    if (!this.hasAddColumnListTarget) return

    const normalizedQuery = this.#normalizeColumnSearch(query)
    let visibleCount = 0
    this.addColumnListTarget.querySelectorAll("button[data-column-key]").forEach((button) => {
      const matches = !normalizedQuery || button.dataset.searchText.includes(normalizedQuery)
      button.hidden = !matches
      if (matches) visibleCount += 1
    })
    this.addColumnListTarget.hidden = visibleCount === 0

    if (this.hasAddColumnEmptyTarget) {
      this.addColumnEmptyTarget.hidden = visibleCount > 0
      this.addColumnEmptyTarget.textContent = normalizedQuery
        ? this.addColumnEmptyTarget.dataset.noResultsLabel
        : this.addColumnEmptyTarget.dataset.noColumnsLabel
    }
  }

  #normalizeColumnSearch(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLocaleLowerCase()
      .trim()
  }

  #showAddColumnView() {
    if (this.hasColumnMainViewTarget) this.columnMainViewTarget.hidden = true
    if (this.hasAddColumnPanelTarget) this.addColumnPanelTarget.hidden = false
    if (this.hasAddColumnSearchTarget) this.addColumnSearchTarget.value = ""
  }

  #showColumnMainView({ restoreFocus = false } = {}) {
    if (this.hasAddColumnPanelTarget) this.addColumnPanelTarget.hidden = true
    if (this.hasColumnMainViewTarget) this.columnMainViewTarget.hidden = false
    if (this.hasAddColumnSearchTarget) this.addColumnSearchTarget.value = ""
    this.#positionViewSettingsMenu()
    if (restoreFocus) {
      const button = this.hasAddColumnButtonTarget
        ? this.addColumnButtonTarget
        : this.viewSettingsMenuTarget.querySelector(".lc-table-view-settings__sticky-action")
      button?.focus()
    }
  }

  #moveColumnByKeyboard(columnKey, direction) {
    const visibleMovableKeys = this.state.order.filter((key) => {
      const column = this.columns.find((item) => item.key === key)
      return column && !column.fixed && !this.state.hidden.includes(key)
    })
    const currentVisibleIndex = visibleMovableKeys.indexOf(columnKey)
    const adjacentKey = visibleMovableKeys[currentVisibleIndex + direction]
    if (currentVisibleIndex < 0 || !adjacentKey) return

    const currentIndex = this.state.order.indexOf(columnKey)
    const adjacentIndex = this.state.order.indexOf(adjacentKey)
    const order = [...this.state.order]
    ;[order[currentIndex], order[adjacentIndex]] = [order[adjacentIndex], order[currentIndex]]
    this.state.order = order
    this.#persistState()
    this.#applyOrder()
    this.#renderColumnMenus()
    this.#updateScrollState()
  }

  #normalizedOrder() {
    return this.state.order
  }

  #columnsSignature() {
    return this.columnStateStore.signature
  }

  #escapeAttribute(value) {
    const element = document.createElement("div")
    element.textContent = value || ""
    return element.innerHTML
  }

  #discardLegacySelectionState() {
    this.selectionStore.discardLegacy()
  }

  #loadSelectedRecords() {
    return this.selectionStore.load()
  }

  // Measured widths only describe the column set and the container width they
  // were taken at: hiding a column or narrowing the window gives every remaining
  // column more room, and carrying those numbers over is what left tables pinned
  // far wider than their content.
  #refreshAutoWidthScope() {
    const scope = `${this.#columnsSignature()}:${this.#measurementContainerWidth()}`
    if (scope === this.autoWidthScope) return

    this.autoWidthScope = scope
    this.autoWidths = this.#loadAutoWidths()
    this.columnWidthsDirty = true
  }

  #measurementContainerWidth() {
    if (!this.verticalScrollContainer) return 0

    return Math.round(
      this.verticalScrollContainer.clientWidth || Number.parseFloat(this.verticalScrollContainer.style.width) || 0
    )
  }

  #autoWidthStorageKey() {
    if (!this.hasStorageKeyValue) return null

    return `${AUTO_WIDTH_STORAGE_PREFIX}:${this.storageKeyValue}:${this.storageVersionValue}`
  }

  // One entry per table, carrying the scope it was measured in. Keying the entry
  // by that scope instead would leave a stale entry behind for every width a
  // window passes through while it is being dragged.
  #loadAutoWidths() {
    const storageKey = this.#autoWidthStorageKey()
    if (!storageKey) return {}

    try {
      const parsed = JSON.parse(window.sessionStorage.getItem(storageKey) || "{}")
      if (!parsed || typeof parsed !== "object") return {}
      if (parsed.scope !== this.autoWidthScope) return {}

      const knownKeys = new Set(this.columns.map((column) => column.key))
      return Object.fromEntries(
        Object.entries(parsed.widths || {}).filter(([key, value]) => {
          const width = Number(value)
          return knownKeys.has(key) && Number.isFinite(width) && width > 0
        })
      )
    } catch (_error) {
      return {}
    }
  }

  #rememberAutoWidth(key, width) {
    if (!Number.isFinite(width) || width <= 0) return
    if ((Number(this.autoWidths[key]) || 0) >= width) return

    this.autoWidths[key] = width
    this.#persistAutoWidths()
  }

  #persistAutoWidths() {
    const storageKey = this.#autoWidthStorageKey()
    if (!storageKey) return

    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify({ scope: this.autoWidthScope, widths: this.autoWidths }))
    } catch (_error) {
      // Ignore private-mode storage failures; column widths can still be measured from the current page.
    }
  }

  #persistSelectedRecords() {
    this.selectionStore.persist(this.selectedRecords)
  }

  #clearSelectedRecords() {
    this.selectedRecords = {}
    this.#persistSelectedRecords()
  }

  #applySelectionState() {
    this.rowCheckboxTargets.forEach((checkbox) => {
      const row = checkbox.closest("tr")
      const recordId = row?.dataset.recordId

      checkbox.checked = Boolean(recordId && this.selectedRecords[recordId])
      this.#toggleRowSelectionState(checkbox)
      this.#captureRowSelectionMetadata(row)
    })
  }

  #syncSelectionRecord(checkbox) {
    const row = checkbox.closest("tr")
    const recordId = row?.dataset.recordId
    if (!recordId) return

    if (checkbox.checked) {
      this.selectedRecords[recordId] = this.#captureRowSelectionMetadata(row)
      return
    }

    delete this.selectedRecords[recordId]
  }

  #captureRowSelectionMetadata(row) {
    if (!row?.dataset.recordId) return null

    const metadata = {
      downloadUrl: row.dataset.downloadUrl || null,
      downloadIsdocUrl: row.dataset.downloadIsdocUrl || null
    }

    if (this.selectedRecords[row.dataset.recordId]) {
      this.selectedRecords[row.dataset.recordId] = {
        ...this.selectedRecords[row.dataset.recordId],
        ...metadata
      }
    }

    return metadata
  }

  #isInteractive(target) {
    return target instanceof Element && Boolean(target.closest(INTERACTIVE_SELECTOR))
  }

  #toggleRowSelectionState(checkbox) {
    checkbox.closest("tr")?.classList.toggle("is-row-selected", checkbox.checked)
  }

  #updateBulkActionAvailability() {
    if (!(this.selectionValue && this.bulkActionsValue)) return
    if (!this.hasSelectionToolbarTarget) return

    const selectedRecords = Object.values(this.selectedRecords)
    const selectedCount = selectedRecords.length

    this.selectionToolbarTarget.querySelectorAll("[data-bulk-action]").forEach((button) => {
      const action = button.dataset.bulkAction
      if (action === "download") {
        button.disabled = !selectedRecords.some((record) => record.downloadUrl)
      } else if (action === "downloadIsdoc") {
        button.disabled = !selectedRecords.some((record) => record.downloadIsdocUrl)
      } else {
        button.disabled = selectedCount === 0
      }
    })

    // The Export dropdown's toggle carries no data-bulk-action (clicking it must
    // open the menu, not run an action), so it is enabled separately.
    this.selectionToolbarTarget.querySelectorAll("[data-bulk-menu-toggle]").forEach((toggle) => {
      toggle.disabled = selectedCount === 0
    })
  }

  #openSelectedUrls(datasetKey) {
    if (!(this.selectionValue && this.bulkActionsValue)) return
    Object.values(this.selectedRecords)
      .map((record) => record?.[datasetKey])
      .filter(Boolean)
      .forEach((url) => {
        window.open(url, "_blank", "noopener")
      })
  }

  #submitBulkDownload(url, button, extraFields = {}) {
    if (!url) {
      showToast({ type: "error", message: "Action could not be completed." })
      return
    }

    const ids = this.selectedIds
    if (ids.length === 0) return

    this.#setBulkDownloadLoading(button)

    const form = document.createElement("form")
    form.method = "post"
    form.action = url
    form.hidden = true
    form.dataset.turbo = "false"

    const csrfInput = document.createElement("input")
    csrfInput.type = "hidden"
    csrfInput.name = "authenticity_token"
    csrfInput.value = getCsrfToken()
    form.appendChild(csrfInput)

    ids.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "ids[]"
      input.value = id
      form.appendChild(input)
    })

    for (const [name, value] of Object.entries(extraFields)) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      form.appendChild(input)
    }

    document.body.appendChild(form)
    form.submit()
    window.setTimeout(() => form.remove(), 0)
    this.#scheduleBulkDownloadLoadingReset(button)
  }

  async #submitBulkMutation(url, ids, method = "POST") {
    if (!url) {
      showToast({ type: "error", message: "Action could not be completed." })
      return
    }

    this.#setBulkActionsBusy(true)
    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken()
        },
        body: JSON.stringify({ ids })
      })

      const responseData = await this.#parseJsonResponse(response)

      if (response.ok) {
        this.#clearSelectedRecords()
        if (responseData.toast) {
          queueToast(PENDING_TOAST_STORAGE_KEY, responseData.toast)
        }
        Turbo.visit(window.location.href, { action: "replace" })
        return
      }

      if (responseData.toast) {
        showToast(responseData.toast)
      } else {
        showToast({
          type: "error",
          message: "Action could not be completed."
        })
      }
    } catch (error) {
      // Tell the user, then re-raise: a bulk mutation that never reached the
      // server is exactly the failure monitoring must see
      // (frontend/stimulus-controllers.md §1).
      showToast({
        type: "error",
        message: "Action could not be completed."
      })
      rethrowUnlessAbort(error)
    } finally {
      this.#setBulkActionsBusy(false)
      this.#updateBulkActionAvailability()
    }
  }

  async #parseJsonResponse(response) {
    const contentType = response.headers?.get?.("content-type") || ""
    if (!contentType.includes("application/json")) return {}

    try {
      return await response.json()
    } catch (_error) {
      return {}
    }
  }

  #setBulkActionsBusy(busy) {
    this.bulkActionInFlight = busy
    if (!this.hasSelectionToolbarTarget) return

    this.selectionToolbarTarget.setAttribute("aria-busy", busy.toString())
    this.selectionToolbarTarget.querySelectorAll("[data-bulk-action]").forEach((button) => {
      button.disabled = busy
      button.setAttribute("aria-busy", busy.toString())
    })
  }

  #setBulkDownloadLoading(button) {
    this.bulkActionInFlight = true
    if (!button) return

    button.disabled = true
    button.setAttribute("aria-busy", "true")
    button.classList.add("is-loading")
    button.dispatchEvent(new CustomEvent("tooltip:set-loading", { bubbles: true }))
  }

  #scheduleBulkDownloadLoadingReset(button) {
    this.cleanupBulkDownloadLoading?.()

    let isReset = false
    const startedAt = Date.now()

    const reset = () => {
      if (isReset) return
      isReset = true
      cleanup()
      this.#resetBulkDownloadLoading(button)
    }

    const resetAfterMinimumDisplay = () => {
      const remaining = Math.max(0, BULK_DOWNLOAD_MIN_LOADING_MS - (Date.now() - startedAt))
      window.setTimeout(reset, remaining)
    }

    const resetOnVisible = () => {
      if (document.visibilityState === "visible") resetAfterMinimumDisplay()
    }

    const timeoutId = window.setTimeout(reset, BULK_DOWNLOAD_MAX_LOADING_MS)
    window.addEventListener("focus", resetAfterMinimumDisplay, { once: true })
    document.addEventListener("visibilitychange", resetOnVisible)

    const cleanup = () => {
      window.clearTimeout(timeoutId)
      window.removeEventListener("focus", resetAfterMinimumDisplay)
      document.removeEventListener("visibilitychange", resetOnVisible)
      if (this.cleanupBulkDownloadLoading === cleanup) this.cleanupBulkDownloadLoading = null
    }

    this.cleanupBulkDownloadLoading = cleanup
  }

  #resetBulkDownloadLoading(button) {
    this.bulkActionInFlight = false
    if (button) {
      button.removeAttribute("aria-busy")
      button.classList.remove("is-loading")
      button.dispatchEvent(new CustomEvent("tooltip:reset", { bubbles: true }))
    }
    this.#updateBulkActionAvailability()
  }

  get selectedIds() {
    if (!this.selectionValue) return []
    return Object.keys(this.selectedRecords)
  }
}
